<template>
	<div class="support-chat-conversation-list flex flex-col h-full p-4">
		<div v-if="loading" class="text-center text-subtle p-4">Loading chats...</div>
		<div v-else-if="conversations.length === 0" class="text-center text-subtle p-4">No previous chats found</div>
		<div v-else class="scrollbar flex flex-col gap-3 overflow-y-auto flex-1 min-h-0 pr-1">
			<div
				v-for="conversation in conversations"
				:key="conversation.ConversationId"
				class="support-chat-conversation-list__item"
			>
				<button
					class="support-chat-conversation-list__item-content button--no-style flex items-center justify-between border border-solid border-primary w-100 p-3 rounded-md hover:bg-hovered"
					:class="{ 'support-chat-conversation-list__item-content--dropdown-open': openDropdownConversationId === conversation.ConversationId }"
					@click="handleConversationClick(conversation.ConversationId, conversation.Summary)"
				>
					<div class="flex flex-col items-start flex-1 min-w-0">
						<input
							v-if="renamingConversationId === conversation.ConversationId"
							:ref="el => renameInput = el"
							v-model="renamingValue"
							class="truncate text-left text-2 mb-1 w-full border border-solid border-input"
							maxlength="100"
							@blur="handleRename(conversation.ConversationId)"
							@keydown.enter="handleRename(conversation.ConversationId)"
							@keydown.escape="handleRenameCancel"
							@click.stop
						/>
						<div v-else class="truncate text-left text-2 mb-1 w-full">{{ conversation.Summary }}</div>
						<div class="text-1 text-subtle">Last message {{ formatDate(conversation.LastUpdated.toString()) }}</div>
					</div>

					<ElDropdown
						class="flex-shrink-0"
						trigger="click"
						@command="onDropdownCommand(conversation.ConversationId, $event)"
						@visible-change="onDropdownVisibilityChange(conversation.ConversationId, $event)"
					>
						<button class="support-chat-conversation-list__trigger button--no-style p-1" @click.stop>
							<Inline svg="ellipsis" />
						</button>
						<ElDropdownMenu>
							<ElDropdownItem command="rename">
								<div class="flex items-center gap-3">
									<Inline class="text-icon-subtle" svg="edit" />
									<span>Rename</span>
								</div>
							</ElDropdownItem>
							<ElDropdownItem command="delete">
								<div class="flex items-center gap-3">
									<Inline class="text-icon-subtle" svg="trash" />
									<span>Delete</span>
								</div>
							</ElDropdownItem>
						</ElDropdownMenu>
					</ElDropdown>
				</button>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent, ref, type PropType, nextTick } from 'vue';
	import { Dropdown, DropdownMenu, DropdownItem } from '@cognitoforms/element-ui';
	import type { Conversation } from '@cognitoforms/types/server-types/model/conversation';

	export default defineComponent({
		name: 'SupportChatConversationList',
		components: {
			ElDropdown: Dropdown,
			ElDropdownMenu: DropdownMenu,
			ElDropdownItem: DropdownItem
		},
		props: {
			conversations: {
				type: Array as PropType<Conversation[]>,
				required: true
			},
			loading: {
				type: Boolean,
				default: false
			}
		},
		emits: ['new-chat', 'load-conversation', 'delete-conversation', 'rename-conversation'],
		setup(props, { emit }) {
			const openDropdownConversationId = ref<string | null>(null);
			const renamingConversationId = ref<string | null>(null);
			const renamingValue = ref('');
			const renameInput = ref(null);

			const formatDate = (dateString: string): string => {
				const date = new Date(dateString);
				const now = new Date();
				const diffMs = now.getTime() - date.getTime();
				const diffMins = Math.floor(diffMs / 60000);
				const diffHours = Math.floor(diffMs / 3600000);
				const diffDays = Math.floor(diffMs / 86400000);

				if (diffMins < 1) return 'just now';
				if (diffMins < 60) return `${diffMins}m ago`;
				if (diffHours < 24) return `${diffHours}h ago`;
				if (diffDays < 7) return `${diffDays}d ago`;

				return date.toLocaleDateString();
			};

			const handleRenameCancel = () => {
				renamingConversationId.value = null;
				renamingValue.value = '';
			};

			const handleRename = (conversationId: string) => {
				if (renamingValue.value.trim() && renamingValue.value.trim() !== '') {
					emit('rename-conversation', conversationId, renamingValue.value.trim());
				}
				setTimeout(() => {
					handleRenameCancel();
				}, 150);
			};

			const startRename = async (conversationId: string) => {
				const conversation = props.conversations.find(c => c.ConversationId === conversationId);
				renamingConversationId.value = conversationId;
				renamingValue.value = conversation.Summary;
				await nextTick();
				if (renameInput.value) {
					renameInput.value.focus();
					renameInput.value.select();
				}
			};

			const onDropdownCommand = (conversationId: string, command: string) => {
				if (command === 'delete') {
					emit('delete-conversation', conversationId);
				}
				else if (command === 'rename') {
					startRename(conversationId);
				}
			};

			const onDropdownVisibilityChange = (conversationId: string, isVisible: boolean) => {
				openDropdownConversationId.value = isVisible ? conversationId : null;
			};

			const handleConversationClick = (conversationId: string, summary: string) => {
				if (renamingConversationId.value !== conversationId) {
					emit('load-conversation', conversationId, summary);
				}
			};

			return {
				formatDate,
				handleConversationClick,
				onDropdownCommand,
				onDropdownVisibilityChange,
				openDropdownConversationId,
				renamingConversationId,
				renamingValue,
				renameInput,
				startRename,
				handleRename,
				handleRenameCancel
			};
		}
	});
</script>

<style lang="scss">
.support-chat-conversation-list {

	&__item {

		&-content {

			&:hover,
			&--dropdown-open {
				background-color: var(--bg-hovered);
				transition: all .2s;

				.support-chat-conversation-list__trigger {
					display: flex;
				}
			}
		}
	}

	&__trigger {
		display: none;
		transition: background .3s .1s;

		&:hover {
			background: var(--bg-hover-in-selected);

			:root & i {
				color: var(--icon-intense);
			}
		}
	}

	&__item-content--dropdown-open &__trigger {
		background: var(--bg-hover-in-selected);

		:root & i {
			color: var(--icon-intense);
		}
	}
}
</style>